import React, { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { calculateSingleProperty, formatValue, formatPercent } from '../../utils';
import { exportReportToPDF, generateDetailedReport } from '../../utils/pdfExport';
import { Download, FileText, Printer } from 'lucide-react';

const Report: React.FC = () => {
  const { currentInputs } = useApp();
  const [image1, setImage1] = useState<string | null>(null);
  const [image2, setImage2] = useState<string | null>(null);
  
  const calculations = calculateSingleProperty(currentInputs);

  if (!calculations) {
    return null;
  }

  // Extract key metrics
  const {
    eigenkapital,
    monatlicheRate,
    gesamtinvestition,
    bruttomietrendite,
    eigenkapitalrendite,
    monatlicherCashflowJahr1IstNachSteuer,
    monatlicherCashflowJahr1IstVorSteuer,
    kaufpreisIst,
    quadratmeterpreisIst,
    projectionData,
    darlehenssumme,
    nebenkosten,
    monatlicheKaltmieteSoll,
    monatlicheKaltmieteIst,
    kaufpreisFaktor,
    anfaenglicheTilgung
  } = calculations;

  // Calculate monthly costs
  const bewirtschaftungskosten = currentInputs.hausgeldUmlagefaehig + currentInputs.hausgeldNichtUmlagefaehig + 
    (currentInputs.instandhaltungsruecklage || 0) + (currentInputs.verwaltungskosten || 0);
  const steuereffekt = monatlicherCashflowJahr1IstNachSteuer - monatlicherCashflowJahr1IstVorSteuer;
  
  // Calculate specific year data
  const year6Data = projectionData[6];
  const year11Data = projectionData[11];
  const year20Data = projectionData[20];
  
  // Format currency with proper German locale
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, setImage: (url: string | null) => void) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExportPDF = async () => {
    await exportReportToPDF('report-content', 'Investment_Report');
  };

  const handleDetailedReport = () => {
    generateDetailedReport(currentInputs, calculations);
  };

  const handlePrint = () => {
    window.print();
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('de-DE');
  };

  return (
    <div className="tab-content active bg-gray-50">
      <div id="report-content" className="max-w-4xl mx-auto bg-white p-6 sm:p-8 rounded-2xl shadow-lg">
        <div className="flex justify-between items-start mb-6 pb-4 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Investitionsübersicht</h2>
            <p className="text-gray-600">{currentInputs.propertyName || 'Nöböbb'}</p>
            <p className="text-sm text-gray-500 mt-1">{getCurrentDate()}</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={handleExportPDF}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white font-semibold rounded-md shadow-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <Download size={16} />
              PDF Export
            </button>
            <button 
              onClick={handleDetailedReport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <FileText size={16} />
              Detailliert
            </button>
            <button 
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white font-semibold rounded-md shadow-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              <Printer size={16} />
              Drucken
            </button>
          </div>
        </div>

        {/* Image Upload Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <label htmlFor="report-image-upload-1" className="h-48 rounded-lg flex items-center justify-center text-gray-400 cursor-pointer border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors overflow-hidden">
            {image1 ? (
              <img src={image1} alt="Bild 1" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                <span>Bild 1 hochladen</span>
              </div>
            )}
          </label>
          <input 
            type="file" 
            id="report-image-upload-1" 
            className="hidden" 
            accept="image/*"
            onChange={(e) => handleImageUpload(e, setImage1)}
          />
          
          <label htmlFor="report-image-upload-2" className="h-48 rounded-lg flex items-center justify-center text-gray-400 cursor-pointer border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors overflow-hidden">
            {image2 ? (
              <img src={image2} alt="Bild 2" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                <span>Bild 2 hochladen</span>
              </div>
            )}
          </label>
          <input 
            type="file" 
            id="report-image-upload-2" 
            className="hidden" 
            accept="image/*"
            onChange={(e) => handleImageUpload(e, setImage2)}
          />
        </div>

        {/* Three Column Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="report-section">
            <h3 className="font-semibold text-gray-700 mb-3">Objektdetails</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Wohnfläche</span>
                <span>{formatValue(currentInputs.wohnflaeche, 'm²')}</span>
              </div>
              <div className="flex justify-between">
                <span>Kaufpreis pro m²</span>
                <span>{formatValue(quadratmeterpreisIst, '€/m²')}</span>
              </div>
              <div className="flex justify-between">
                <span>Baujahr</span>
                <span>{currentInputs.baujahr}</span>
              </div>
              <div className="flex justify-between">
                <span>Lage</span>
                <span>{currentInputs.lage}</span>
              </div>
            </div>
          </div>

          <div className="report-section">
            <h3 className="font-semibold text-gray-700 mb-3">Investition</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Kaufpreis</span>
                <span>{formatCurrency(kaufpreisIst)}</span>
              </div>
              <div className="flex justify-between">
                <span>Kaufnebenkosten</span>
                <span>{formatCurrency(nebenkosten)}</span>
              </div>
              <div className="flex justify-between">
                <span>Zusatzl. Investitionsbedarf</span>
                <span>{formatCurrency(currentInputs.anfangsinvestitionen)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t font-bold">
                <span>Gesamtinvestition</span>
                <span>{formatCurrency(gesamtinvestition)}</span>
              </div>
            </div>
          </div>

          <div className="report-section">
            <h3 className="font-semibold text-gray-700 mb-3">Finanzierungsstruktur</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Darlehenssumme</span>
                <span>{formatCurrency(darlehenssumme)}</span>
              </div>
              <div className="flex justify-between">
                <span>Finanzierungsanteil</span>
                <span>{formatPercent(currentInputs.finanzierungsanteil)}</span>
              </div>
              <div className="flex justify-between">
                <span>Zinssatz (p.a.)</span>
                <span>{formatPercent(currentInputs.zinssatz)}</span>
              </div>
              <div className="flex justify-between">
                <span>Anfängliche Tilgung (p.a.)</span>
                <span>{formatPercent(anfaenglicheTilgung)}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Eigenkapital</span>
                <span>{formatCurrency(eigenkapital)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="report-section">
            <h3 className="font-semibold text-gray-700 mb-3">Rentabilität</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Monatl. Nettokaltmiete (SOLL)</span>
                <span>{formatCurrency(monatlicheKaltmieteSoll)}</span>
              </div>
              <div className="flex justify-between">
                <span>Bruttomietrendite</span>
                <span>{formatPercent(bruttomietrendite)}</span>
              </div>
              <div className="flex justify-between">
                <span>Kaufpreis-Faktor</span>
                <span>{kaufpreisFaktor.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>EK-Rendite (Jahr 1)</span>
                <span>{formatPercent(eigenkapitalrendite)}</span>
              </div>
            </div>
          </div>

          <div className="report-section">
            <h3 className="font-semibold text-gray-700 mb-3">Monatlicher Cashflow (Jahr 1)</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Nettokaltmiete</span>
                <span className="text-green-700">+ {formatCurrency(monatlicheKaltmieteIst)}</span>
              </div>
              <div className="flex justify-between">
                <span>Bewirtschaftungskosten</span>
                <span className="text-red-700">- {formatCurrency(bewirtschaftungskosten)}</span>
              </div>
              <div className="flex justify-between">
                <span>Zinsen & Tilgung</span>
                <span className="text-red-700">- {formatCurrency(monatlicheRate)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span>= Cashflow vor Steuern</span>
                <span className="font-bold">{formatCurrency(monatlicherCashflowJahr1IstVorSteuer)}</span>
              </div>
              <div className="flex justify-between">
                <span>Steuereffekt</span>
                <span className={steuereffekt >= 0 ? 'text-green-700' : 'text-red-700'}>
                  {steuereffekt >= 0 ? '+' : ''} {formatCurrency(steuereffekt)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t font-bold">
                <span>= Cashflow nach Steuern</span>
                <span className={monatlicherCashflowJahr1IstNachSteuer >= 0 ? 'text-green-700' : 'text-red-700'}>
                  {formatCurrency(monatlicherCashflowJahr1IstNachSteuer)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Development Summary */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="font-semibold text-gray-700 mb-3">Zusammenfassung der Entwicklung</h3>
          
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-semibold mb-1">Nach 6 Jahren:</p>
              <div className="pl-4 text-gray-600">
                <p>A) Immobilien Wert {formatCurrency(year6Data?.immobilienwert || 0)} €; minus Restschuld {formatCurrency(year6Data?.restschuld || 0)} € = {formatCurrency((year6Data?.immobilienwert || 0) - (year6Data?.restschuld || 0))} €</p>
                <p>B) Kum. Cashflow: {formatCurrency(year6Data?.cumulativeCashflowNachSteuer || 0)} €; davon initial-EK {formatCurrency(eigenkapital)} € & Rückzahlung: {formatCurrency((year6Data?.cumulativeCashflowNachSteuer || 0) - eigenkapital)} €</p>
                <p className="text-xs text-gray-500 mt-1">... das macht eine jährliche Rendite von {formatPercent((year6Data?.eigenkapitalrendite || 0) / 6)} %.</p>
                <p>C) Monatl. Cashflow v.St.: {formatCurrency(year6Data?.monatlicherCashflowVorSteuer || 0)} € und n.St: {formatCurrency(year6Data?.monatlicherCashflowNachSteuer || 0)} €</p>
              </div>
            </div>

            <div>
              <p className="font-semibold mb-1">Nach 11 Jahren:</p>
              <div className="pl-4 text-gray-600">
                <p>A) Immobilien Wert {formatCurrency(year11Data?.immobilienwert || 0)} €; minus Restschuld {formatCurrency(year11Data?.restschuld || 0)} € = {formatCurrency((year11Data?.immobilienwert || 0) - (year11Data?.restschuld || 0))} €</p>
                <p>B) Kum. Cashflow: {formatCurrency(year11Data?.cumulativeCashflowNachSteuer || 0)} €; davon initial-EK {formatCurrency(eigenkapital)} € & Rückzahlung: {formatCurrency((year11Data?.cumulativeCashflowNachSteuer || 0) - eigenkapital)} €</p>
                <p className="text-xs text-gray-500 mt-1">... das macht eine jährliche Rendite von {formatPercent((year11Data?.eigenkapitalrendite || 0) / 11)} %.</p>
                <p>C) Monatl. Cashflow v.St.: {formatCurrency(year11Data?.monatlicherCashflowVorSteuer || 0)} € und n.St: {formatCurrency(year11Data?.monatlicherCashflowNachSteuer || 0)} €</p>
              </div>
            </div>

            <div>
              <p className="font-semibold mb-1">Nach 20 Jahren:</p>
              <div className="pl-4 text-gray-600">
                <p>A) Immobilien Wert {formatCurrency(year20Data?.immobilienwert || 0)} €; minus Restschuld {formatCurrency(year20Data?.restschuld || 0)} € = {formatCurrency((year20Data?.immobilienwert || 0) - (year20Data?.restschuld || 0))} €</p>
                <p>B) Kum. Cashflow: {formatCurrency(year20Data?.cumulativeCashflowNachSteuer || 0)} €; davon initial-EK {formatCurrency(eigenkapital)} € & Rückzahlung: {formatCurrency((year20Data?.cumulativeCashflowNachSteuer || 0) - eigenkapital)} €</p>
                <p className="text-xs text-gray-500 mt-1">... das macht eine jährliche Rendite von {formatPercent((year20Data?.eigenkapitalrendite || 0) / 20)} %.</p>
                <p>C) Monatl. Cashflow v.St.: {formatCurrency(year20Data?.monatlicherCashflowVorSteuer || 0)} € und n.St: {formatCurrency(year20Data?.monatlicherCashflowNachSteuer || 0)} €</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional notes if present */}
        {(currentInputs.positiveMerkmale || currentInputs.negativeMerkmale) && (
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-gray-700 mb-3">Zusätzliche Bewertung</h3>
            {currentInputs.positiveMerkmale && (
              <div className="mb-3">
                <h4 className="font-semibold text-green-600 mb-1">Positive Merkmale:</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{currentInputs.positiveMerkmale}</p>
              </div>
            )}
            {currentInputs.negativeMerkmale && (
              <div>
                <h4 className="font-semibold text-red-600 mb-1">Negative Merkmale:</h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{currentInputs.negativeMerkmale}</p>
              </div>
            )}
          </div>
        )}

        {/* Footer note */}
        <div className="text-xs text-gray-500 text-center mt-6 pt-4 border-t">
          <p>Diese Übersicht dient als erste Einschätzung. Bitte konsultieren Sie einen Fachexperten für eine detaillierte Analyse.</p>
        </div>
      </div>
    </div>
  );
};

export default Report;